import { Text } from "@overmap-ai/blocks"
import type { ProcedureType, Stored } from "@overmap-ai/common"
import {
	type ColumnDef,
	getCoreRowModel,
	getSortedRowModel,
	type TableOptions,
	useReactTable,
} from "@tanstack/react-table"
import { useMemo } from "react"

import { formatTimestampDate } from "../../../utils/date"
import { ProcedureTypeIcon } from "../../Icons/ProcedureTypeIcon"
import { ColumnSortButton, ColumnSortIcon } from "../components"

export const useProcedureTypeSettingsTableColumns = () => {
	return useMemo<ColumnDef<Stored<ProcedureType>>[]>(
		() => [
			{
				id: "name",
				accessorKey: "name",
				enableHiding: true,
				meta: {
					plainHeader: "Name",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Name
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: function Cell({ row }) {
					const procedureType = row.original

					return (
						<div className="flex items-center gap-2">
							<ProcedureTypeIcon procedureTypeId={procedureType.uuid} background={false} />
							<Text>{procedureType.name}</Text>
						</div>
					)
				},
			},
			{
				id: "submitted_at",
				accessorFn: (procedureType) => procedureType.submitted_at,
				meta: {
					plainHeader: "Created at",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Created at
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					return <Text accentColor="base">{formatTimestampDate(getValue<string>())}</Text>
				},
			},
		],
		[],
	)
}

export const useProcedureTypeSettingsTable = (args: Omit<TableOptions<Stored<ProcedureType>>, "getCoreRowModel">) => {
	return useReactTable<Stored<ProcedureType>>({
		...args,
		getRowId: (originalRow) => originalRow.uuid,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
	})
}
